/**
 * WorkflowConditionField - Campo de filtros do inspetor (resumo + modal)
 *
 * Padrão adotado no designer: no sheet aparece apenas o RESUMO das regras
 * (fichas legíveis) e a edição acontece no modal amplo
 * (`WorkflowConditionDialog`), que tem espaço para conjuntos E/OU.
 *
 * @version 1.0.0
 * RELEASE: 2026-09-01 v1.0.0 - Criação inicial (filtros robustos do gatilho)
 */

import { useState } from 'react';
import { Filter, Pencil } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

import { Ajuda } from './Ajuda';
import { WorkflowConditionSummary } from './WorkflowConditionSummary';
import { WorkflowConditionDialog } from './WorkflowConditionDialog';
import { contarRegras, type CondicaoWorkflow } from './WorkflowConditionBuilder';

interface Props {
  valor: unknown;
  onChange: (condicao: CondicaoWorkflow) => void;
  /** Rótulo exibido acima do resumo */
  titulo?: string;
  /** Texto do "?" ao lado do rótulo */
  ajuda?: string;
  /** Título do modal de edição */
  tituloModal?: string;
  /** Texto exibido quando não há regra */
  vazio?: string;
  /** Entidade-raiz do processo (crm_deal | billing). Padrão: Negócio */
  entidade?: string | null;
}

export function WorkflowConditionField({
  valor,
  onChange,
  titulo = 'Condições',
  ajuda,
  tituloModal = 'Configurar filtros',
  vazio,
  entidade,
}: Props) {
  const [aberto, setAberto] = useState(false);
  const total = contarRegras(valor);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label className="flex items-center gap-1.5 text-xs font-medium">
          {titulo}
          {ajuda && <Ajuda texto={ajuda} />}
        </Label>
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          onClick={() => setAberto(true)}
        >
          {total > 0 ? <Pencil className="h-3 w-3" /> : <Filter className="h-3 w-3" />}
          {total > 0 ? `Editar (${total})` : 'Adicionar filtro'}
        </Button>
      </div>

      <WorkflowConditionSummary condicoes={valor} vazio={vazio} />

      <WorkflowConditionDialog
        open={aberto}
        onOpenChange={setAberto}
        valor={valor}
        onSalvar={onChange}
        title={tituloModal}
        entidade={entidade}
      />
    </div>
  );
}
